"use client";

/**
 * NavOverflowMenu
 *
 * The desktop navbar's "priority+" overflow dropdown: holds the nav items that don't fit inline.
 * Opens on hover (and tap on coarse-pointer devices), closes on outside click or after a short
 * delay — mirroring {@link ./NavDropdown}'s interaction — but accepts the full {@link NavItem} shape
 * (flat links, cross-page links, and nested dropdown groups) rather than anchor-only children.
 *
 * Rendered only when the bar actually overflows; dividers in the tail are skipped inside the panel.
 */

import type React from "react";
import { useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { scrollToAnchor } from "../../../../_logic/utils/scroll-utils";
import { NAV_DROPDOWN_ANCHOR_GAP_PX } from "../../../../_logic/constants";
import { useNavDisclosure } from "../../../../_hooks/useNavDisclosure";
import type { NavItem } from "./nav-types";

interface NavOverflowMenuProps {
  /** Trigger label (e.g. "More"). */
  label: string;
  /** The tail of nav items that didn't fit inline. */
  items: NavItem[];
  /** Whether this is the public (interactive) view; the editor renders a static trigger. */
  isPublic: boolean;
  /** Trigger text color (themed); matches the inline links' color. */
  triggerColor: string;
}

/** Floating overflow menu for the desktop navbar. */
export function NavOverflowMenu({ label, items, isPublic, triggerColor }: NavOverflowMenuProps): React.ReactElement {
  // States
  const { isOpen, containerRef, containerProps, triggerProps, close } = useNavDisclosure();

  // Handlers
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault();
      scrollToAnchor(sectionId, NAV_DROPDOWN_ANCHOR_GAP_PX);
      close();
    },
    [close]
  );

  // Builder mode — non-interactive trigger only.
  if (!isPublic) {
    return (
      <span className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-gray-600">
        {label}
        <ChevronDown className="h-3.5 w-3.5" />
      </span>
    );
  }

  // Render
  return (
    <div className="relative" ref={containerRef} {...containerProps}>
      <button
        type="button"
        {...triggerProps}
        className="flex touch-manipulation items-center gap-1 whitespace-nowrap text-sm font-semibold transition-opacity hover:opacity-80"
        style={{ color: triggerColor }}
        aria-haspopup="menu"
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full z-50 mt-1 max-h-[70vh] min-w-[200px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="menu"
        >
          {items.map((item, idx) => {
            if (item.type === "divider") return null;

            if (item.type === "dropdown") {
              return (
                <div key={`dropdown-${item.label}-${idx}`} role="group" aria-label={item.label}>
                  <span className="block px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {item.label}
                  </span>
                  {item.children.map((child) => (
                    <a
                      key={child.id}
                      href={`#${child.id}`}
                      onClick={(e) => handleAnchorClick(e, child.id)}
                      className="flex min-h-[44px] touch-manipulation items-center px-4 py-2 pl-6 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                      role="menuitem"
                    >
                      {child.label}
                    </a>
                  ))}
                </div>
              );
            }

            if (item.type === "page-link") {
              return (
                <a
                  key={`page-${item.href}-${idx}`}
                  href={item.href}
                  className={`flex min-h-[44px] touch-manipulation items-center px-4 py-2 text-sm transition-colors hover:bg-gray-50 hover:text-gray-900 ${
                    item.isCurrent ? "font-bold text-gray-900" : "font-medium text-gray-600"
                  }`}
                  role="menuitem"
                >
                  {item.label}
                </a>
              );
            }

            return (
              <a
                key={`${item.id}-${idx}`}
                href={`#${item.id}`}
                onClick={(e) => handleAnchorClick(e, item.id)}
                className="flex min-h-[44px] touch-manipulation items-center px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                role="menuitem"
              >
                {item.label}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
